import React, { useEffect, useRef, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Statistic, Row, Col, Button, Space, message } from 'antd';
import jspdf from 'jspdf/dist/jspdf.umd.min.js';
import html2canvas from 'html2canvas';
import api from '../../../utils/api';
const { jsPDF } = jspdf;

const { Title, Paragraph } = Typography;

const RevenueReports = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState({ mrr: 0, arr: 0, arpu: 0 });
  const [rows, setRows] = useState([]);
  const reportRef = useRef(null);

  const columns = [
    { title: 'Month', dataIndex: 'month', key: 'month' },
    { title: 'New MRR', dataIndex: 'new_mrr', key: 'new_mrr', render: (v) => `$${v.toLocaleString()}` },
    { title: 'Churned', dataIndex: 'churn', key: 'churn', render: (v) => `$${v.toLocaleString()}` },
    { title: 'Net MRR', dataIndex: 'net_mrr', key: 'net_mrr', render: (v) => `$${v.toLocaleString()}` },
  ];

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const resp = await api.get('/analytics/revenue-reports?months=6');
        if (!mounted) return;
        setSummary(resp?.summary || { mrr: 0, arr: 0, arpu: 0 });
        setRows(Array.isArray(resp?.items) ? resp.items : []);
      } catch (e) {
        // Optional: could show message.error
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const handleExportCSV = () => {
    try {
      const header = ['Month','New MRR','Churned','Net MRR'];
      const lines = [header.join(',')];
      for (const r of rows) {
        const row = [r.month, r.new_mrr, r.churn, r.net_mrr].map((v) => (v ?? ''));
        lines.push(row.join(','));
      }
      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue-report-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Report downloaded as CSV');
    } catch (e) {
      message.error(e?.message || 'Failed to export CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      if (!reportRef.current) return;
      setExporting(true);
      // Use white background to avoid transparency artifacts
      const canvas = await html2canvas(reportRef.current, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let remainingHeight = imgHeight;
      const pageImgHeight = pageHeight;

      // If content exceeds one page, slice into pages
      if (imgHeight <= pageImgHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        // Create pages by drawing portions
        let sY = 0;
        const onePageCanvas = document.createElement('canvas');
        const ctx = onePageCanvas.getContext('2d');
        const ratio = imgWidth / canvas.width; // scale ratio
        const pageCanvasHeight = Math.floor((pageHeight / ratio));
        onePageCanvas.width = canvas.width;
        onePageCanvas.height = pageCanvasHeight;

        while (remainingHeight > 0) {
          // Draw next slice
          ctx.clearRect(0, 0, onePageCanvas.width, onePageCanvas.height);
          ctx.drawImage(canvas, 0, sY, canvas.width, pageCanvasHeight, 0, 0, canvas.width, pageCanvasHeight);
          const pageData = onePageCanvas.toDataURL('image/png');
          pdf.addImage(pageData, 'PNG', 0, 0, imgWidth, pageHeight);
          remainingHeight -= pageHeight;
          sY += pageCanvasHeight;
          if (remainingHeight > 0) pdf.addPage();
        }
      }
      const filename = `revenue-report-${new Date().toISOString().slice(0,10)}.pdf`;
      pdf.save(filename);
      message.success('Report exported as PDF');
    } catch (e) {
      message.error(e?.message || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Admin' }, { title: 'Analytics' }, { title: 'Revenue Reports' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Revenue Reports</Title>
        <Space>
          <Button onClick={handleExportCSV} disabled={loading}>Download CSV</Button>
          <Button type="primary" onClick={handleExportPDF} loading={exporting} disabled={loading}>Generate PDF</Button>
        </Space>
      </div>

      <div ref={reportRef}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}><Card><Statistic title="MRR" value={`$${summary.mrr.toLocaleString()}`} /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title="ARR" value={`$${summary.arr.toLocaleString()}`} /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title="ARPU" value={`$${summary.arpu.toLocaleString()}`} /></Card></Col>
      </Row>

      <Card
        title="Monthly Revenue"
        extra={
          React.createElement(Space, null,
            React.createElement(Button, { onClick: handleExportCSV, disabled: loading }, 'Download CSV'),
            React.createElement(Button, { type: 'primary', onClick: handleExportPDF, loading: exporting, disabled: loading }, 'Generate PDF')
          )
        }
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Monthly revenue breakdown from invoices (paid).</Paragraph>
        <Table rowKey="month" columns={columns} dataSource={rows} loading={loading} pagination={false} />
      </Card>
      </div>
    </div>
  );
};

export default RevenueReports;
